/**
 * Tab navigation "Tragbarkeitsrechner | Zinsvergleich" (W6b).
 *
 * One embed, two views: switching toggles the [hidden] attribute only — no
 * reload, no re-mount. The iframe height follows automatically through the
 * existing ResizeObserver in embed.ts, so the WP page never jumps.
 *
 * The start view comes from the `view` query param ('rechner' default); the
 * embed snippet maps the host's data-view attribute onto that param.
 */
export type ViewName = 'rechner' | 'vergleich';

export interface Tabs {
  switchTo(view: ViewName): void;
}

const VIEWS: readonly ViewName[] = ['rechner', 'vergleich'];

export function startView(): ViewName {
  const v = new URLSearchParams(location.search).get('view');
  return v === 'vergleich' ? 'vergleich' : 'rechner';
}

/** `onShow` fires on EVERY activation (incl. the initial one); views keep their own first-open state. */
export function initTabs(onShow: (view: ViewName) => void): Tabs {
  const tab = (v: ViewName): HTMLButtonElement =>
    document.getElementById(`tab-${v}`) as HTMLButtonElement;
  const panel = (v: ViewName): HTMLElement => document.getElementById(`view-${v}`) as HTMLElement;

  let active: ViewName | null = null;

  const switchTo = (view: ViewName): void => {
    if (view === active) return;
    active = view;
    for (const v of VIEWS) {
      const on = v === view;
      panel(v).hidden = !on;
      tab(v).classList.toggle('is-active', on);
      tab(v).setAttribute('aria-selected', on ? 'true' : 'false');
      tab(v).tabIndex = on ? 0 : -1;
    }
    onShow(view);
  };

  for (const v of VIEWS) {
    tab(v).addEventListener('click', () => switchTo(v));
  }
  // Roving-tabindex arrow keys (WAI-ARIA tabs pattern, horizontal).
  document.querySelector('.tabs')?.addEventListener('keydown', (e) => {
    const key = (e as KeyboardEvent).key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    const next = active === 'rechner' ? 'vergleich' : 'rechner';
    switchTo(next);
    tab(next).focus();
  });

  switchTo(startView());
  return { switchTo };
}
